import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import StatusBadge from '../../../components/portal/common/StatusBadge';
import AdvancedLessonCard from '../../../components/portal/AdvancedLessonCard';
import { useAppSelector } from '../../../store/hooks';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { LessonFilter } from '../../../hooks/useFilters';
import { apiService } from '../../../services/api';
import { useSystemParameters } from '../../../contexts/ParametersContext';
import BottomPager from '../../../components/portal/common/BottomPager';
import {lessonDetailPath} from "../../../utils/lessonUtils";
import { useTranslation } from 'react-i18next';
import BulkLessonCreateModal from '../../../components/portal/teacher/BulkLessonCreateModal';

export type RoleMode = 'student' | 'teacher' | 'admin';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const ContentWrapper = styled.div`
    position: relative;
    z-index: 3;
    max-width: 1200px;
    margin: 0 auto;
    padding: 6rem var(--desktop-page-padding-x, 2rem) 2rem;

    @media (max-width: 768px) {
      max-width: none;
      margin: 0;
      padding: 0 var(--mobile-page-padding-x);
      width: 100%;
      padding-top: var(--mobile-page-padding-top);
    }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h1 {
    color: #111827;
    margin: 0;
    font-size: 1.75rem;
    font-weight: 800;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    h1 { font-size: 1.5rem; }
  }
`;

const BulkButton = styled.button`
  background: var(--primary-orange);
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0,0,0,0.15);
  }
`;

const SectionCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.06);
  margin-bottom: 1rem;
  padding: 1rem;
`;

const LessonItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 12px;
  background: #f9fafb;
  border: 1px solid rgba(0, 0, 0, 0.03);
  margin-bottom: 0.5rem;
`;

const StatusPill = (status: string) => {
  switch (String(status).toLowerCase()) {
    case 'completed':
      return { background: 'rgba(17,153,142,0.12)', color: '#11998e' };
    case 'cancelled':
      return { background: 'rgba(252,74,26,0.12)', color: '#fc4a1a' };
    case 'in_progress':
      return { background: 'rgba(255,206,84,0.12)', color: '#f1c40f' };
    default:
      return { background: 'rgba(102,126,234,0.12)', color: '#667eea' };
  }
};

const toTitle = (s: any) => {
  if (!s) return '';
  const str = String(s).replace(/_/g, ' ').toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const sameAmsterdamDay = (aIso: string, bIso: string) => {
  try {
    const a = new Date(aIso).toLocaleDateString('nl-NL', { timeZone: 'Europe/Amsterdam' });
    const b = new Date(bIso).toLocaleDateString('nl-NL', { timeZone: 'Europe/Amsterdam' });
    return a === b;
  } catch {
    return aIso.slice(0, 10) === bIso.slice(0, 10);
  }
};

export type UnifiedLessonsProps = {
  roleMode?: RoleMode;
};

import { FilterList as FilterIcon } from '@mui/icons-material';
import { useFilters } from '../../../hooks/useFilters';
import LessonListFilters from '../../../components/portal/common/LessonListFilters';

const FilterToggleBtn = styled.button<{ $active?: boolean }>`
  display: none;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  border-radius: 12px;
  border: 1.5px solid ${props => props.$active ? 'var(--primary-orange)' : '#e5e7eb'};
  background: ${props => props.$active ? 'rgba(249, 115, 22, 0.05)' : '#fff'};
  color: ${props => props.$active ? 'var(--primary-orange)' : '#4b5563'};
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    display: flex;
    width: 100%;
    justify-content: center;
  }

  &:hover {
    border-color: var(--primary-orange);
    color: var(--primary-orange);
  }
`;

const FilterWrapper = styled.div<{ $show?: boolean }>`
  @media (max-width: 768px) {
    display: ${props => props.$show ? 'block' : 'none'};
    margin-bottom: 1.5rem;
  }
`;

const TabGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Tab = styled.button<{ $active?: boolean }>`
  padding: 0.4rem 0.85rem;
  border-radius: 10px;
  border: none;
  background: ${({ $active }) => ($active ? 'var(--primary-orange)' : '#f3f4f6')};
  color: ${({ $active }) => ($active ? 'white' : '#374151')};
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--primary-orange)' : '#e5e7eb')};
  }
`;

const UnifiedLessons: React.FC<UnifiedLessonsProps> = ({ roleMode }) => {
  const { t } = useTranslation();
  const DEFAULT_PAGE_SIZE = 10;
  const [pageBooked, setPageBooked] = React.useState(0);
  const [pageAvailable, setPageAvailable] = React.useState(0);
  const [pagePast, setPagePast] = React.useState(0);
  const pageSize = DEFAULT_PAGE_SIZE;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((s) => s.auth);

  const resolvedRole: RoleMode = (roleMode ?? (String(user?.role || 'student').toLowerCase() as RoleMode));
  const canBookCancel = resolvedRole === 'student';

  const [myLessons, setMyLessons] = React.useState<any[]>([]);
  const [availableLessons, setAvailableLessons] = React.useState<any[]>([]);
  const [lessonsLoading, setLessonsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [booking, setBooking] = React.useState<string | null>(null);
  const [cancelling, setCancelling] = React.useState<string | null>(null);
  const [successMsg, setSuccessMsg] = React.useState<string | null>(null);

  const {
    lessonFilter: listTab,
    setLessonFilter: setListTab,
    selectedDate: selectedAvailDate,
    setSelectedDate: setSelectedAvailDate,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    lessonType,
    setLessonType,
    licenseType,
    setLicenseType,
    appliedFilters,
    applyFilters,
    resetFilters,
  } = useFilters({
    initialLessonFilter: 'all',
    initialStartDate: new Date().toISOString().split('T')[0],
  });

  const [isBulkModalOpen, setIsBulkModalOpen] = React.useState(false);
  const [showFiltersMobile, setShowFiltersMobile] = React.useState(false);

  // Note editing moved to unified detail page; list page no longer supports editing notes

  const pad2 = (n: number) => String(n).padStart(2, '0');
  const isStudent = resolvedRole === 'student';
  const todayStr = React.useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }, []);

  // Honor URL hash to select tab (e.g., #available)
  React.useEffect(() => {
    const hash = String(location.hash || '').toLowerCase();
    if (hash === '#available') setListTab('available');
    else if (hash === '#all') setListTab('all');
  }, [location.hash]);

  const { getNumber } = useSystemParameters();
  const effectiveDailyCap = React.useMemo(() => getNumber('lessons.per.day', 2), [getNumber]);

  const mapLesson = (l: any) => ({
    id: String(l.id ?? l.lessonId ?? ''),
    studentId: l.student?.id ?? l.studentId ?? null,
    teacherId: l.teacher?.id ?? l.teacherId ?? null,
    teacherName: (() => {
      const t = l.teacher;
      if (t) {
        const f = (t.firstName ?? '').trim();
        const ls = (t.lastName ?? '').trim();
        if (!f || !ls) return (t.username ?? `${f} ${ls}`.trim());
        return `${f} ${ls}`.trim();
      }
      return l.teacherName ?? undefined;
    })(),
    studentName: (() => {
      const s = l.student;
      if (s) {
        const f = (s.firstName ?? '').trim();
        const ls = (s.lastName ?? '').trim();
        if (!f || !ls) return (s.username ?? `${f} ${ls}`.trim());
        return `${f} ${ls}`.trim();
      }
      return l.studentName ?? l.student?.studentName ?? undefined;
    })(),
    dateTime: l.lessonDateTime ?? l.dateTime,
    duration: l.durationMinutes ?? l.duration ?? 60,
    status: (typeof l.isBooked === 'boolean') ? (l.isBooked ? 'scheduled' : 'available') : (l.status ?? 'scheduled'),
    type: l.lessonType ?? l.type ?? 'REGULAR',
    location: l.location ?? 'Amsterdam',
    teacherNote: l.teacherNote ?? undefined,
    studentNote: l.studentNote ?? undefined,
  });

  const refreshLessons = React.useCallback(async () => {
    setLessonsLoading(true);
    setError(null);
    try {
      let myList: any[] = [];
      let avListRaw: any[] = [];

      const { 
        lessonFilter: appliedFilter,
        selectedDate: appliedSelectedAvailDate,
        startDate: appliedFrom,
        endDate: appliedTo,
        lessonType: appliedLessonType,
        licenseType: appliedLicenseType,
      } = appliedFilters;

      // Handle "Day" filter by converting it to a range if set
      const fromDate = appliedSelectedAvailDate ? `${appliedSelectedAvailDate}T00:00:00` : appliedFrom;
      const toDate = appliedSelectedAvailDate ? `${appliedSelectedAvailDate}T23:59:59` : appliedTo;

      const params = new URLSearchParams();
      if (appliedFilter && appliedFilter !== 'all') params.append('filter', appliedFilter);
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);
      if (appliedLessonType) params.append('lessonType', appliedLessonType);
      if (appliedLicenseType) params.append('licenseType', appliedLicenseType);
      params.append('size', '1000');

      // Booked/Past per role
      const isAvailableTab = appliedFilter === 'available';
      
      if (resolvedRole === 'student') {
        const my = await apiService.get<any>(`${API_ENDPOINTS.LESSONS.ALL}/my-lessons?${params.toString()}`);
        myList = Array.isArray(my?.data) ? my.data : Array.isArray(my) ? my : [];
      } else if (resolvedRole === 'teacher') {
        const resp = await apiService.get<any>(`${API_ENDPOINTS.LESSONS.TEACHER_LESSONS}?${params.toString()}`);
        myList = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      } else if (resolvedRole === 'admin') {
        const resp = await apiService.get<any>(`${API_ENDPOINTS.ADMIN.LESSONS_PAGE}?page=0&sortBy=lessonDateTime&sortDir=asc&${params.toString()}`);
        const list = Array.isArray(resp?.lessons) ? resp.lessons : Array.isArray(resp?.data?.lessons) ? resp.data.lessons : [];
        myList = list;
      }

      // Available per role
      if (isAvailableTab || appliedSelectedAvailDate || appliedFilter === 'all') {
        let endpoint = (resolvedRole === 'student') ? API_ENDPOINTS.LESSONS.MY_AVAILABLE : API_ENDPOINTS.LESSONS.AVAILABLE;
        
        // Use correct query parameters for my-available
        const avParams = new URLSearchParams(params);
        if (resolvedRole === 'student') {
            if (fromDate) avParams.set('from', fromDate);
            if (toDate) avParams.set('to', toDate);
        }
        
        const av = await apiService.get<any>(`${endpoint}?${avParams.toString()}`);
        avListRaw = Array.isArray(av?.data) ? av.data : Array.isArray(av) ? av : [];
      }

      const myMapped = myList.map(mapLesson);
      const avMapped = avListRaw.map(mapLesson);

      let avFiltered = avMapped;
      if (resolvedRole === 'student') {
        const assignedTeacherId = user?.assignedTeacherId ? String(user.assignedTeacherId) : null;
        avFiltered = assignedTeacherId ? avMapped.filter((l: any) => String(l.teacherId ?? '') === assignedTeacherId) : [];
      } else if (resolvedRole === 'teacher') {
        const teacherId = user?.id ? String(user.id) : null;
        avFiltered = teacherId ? avMapped.filter((l: any) => String(l.teacherId ?? '') === teacherId) : [];
      } else {
        avFiltered = avMapped;
      }

      const ascend = (a: any, b: any) => {
        const ta = a?.dateTime ? Date.parse(a.dateTime) : Number.MAX_SAFE_INTEGER;
        const tb = b?.dateTime ? Date.parse(b.dateTime) : Number.MAX_SAFE_INTEGER;
        return ta - tb;
      };

      setMyLessons([...myMapped].sort(ascend));
      setAvailableLessons([...avFiltered].sort(ascend));
    } catch (e: any) {
      setError(e?.message || 'Failed to load lessons');
    } finally {
      setLessonsLoading(false);
    }
  }, [roleMode, appliedFilters, user?.assignedTeacherId, user?.id, resolvedRole]);

  useEffect(() => {
    if (user?.id) {
      refreshLessons();
    }
  }, [user?.id, refreshLessons]);

  // Ensure we fetch fresh available lessons when the Available tab is shown
  useEffect(() => {
    if (appliedFilters.lessonFilter === 'available' && user?.id) {
      refreshLessons();
    }
  }, [appliedFilters.lessonFilter, user?.id, refreshLessons]);

  const upcomingLessons = React.useMemo(() => {
    return myLessons.filter(l => {
      const isFuture = l.dateTime ? new Date(l.dateTime).getTime() > Date.now() : false;
      const status = String(l.status || '').toLowerCase();
      // For booked tab, we want scheduled/booked items in the future
      return (status === 'scheduled' || status === 'booked') && isFuture;
    });
  }, [myLessons]);

  const pastLessons = React.useMemo(() => {
    return myLessons.filter(l => {
      const isPast = l.dateTime ? new Date(l.dateTime).getTime() <= Date.now() : false;
      const status = String(l.status || '').toLowerCase();
      // For past tab, we want items in the past OR specifically marked as completed/cancelled
      return isPast || status === 'completed' || status === 'cancelled';
    });
  }, [myLessons]);

  const filteredAvailable = React.useMemo(() => {
    return availableLessons;
  }, [availableLessons]);

  // Pagination helpers per tab
  const allLessonsCombined = React.useMemo(() => {
    const combined = [...myLessons, ...availableLessons];
    // De-duplicate if somehow the same lesson is in both (though they shouldn't be as one is booked, one is available)
    const seen = new Set();
    const unique = combined.filter(l => {
      if (seen.has(l.id)) return false;
      seen.add(l.id);
      return true;
    });
    return unique.sort((a, b) => {
      const ta = a?.dateTime ? Date.parse(a.dateTime) : Number.MAX_SAFE_INTEGER;
      const tb = b?.dateTime ? Date.parse(b.dateTime) : Number.MAX_SAFE_INTEGER;
      return ta - tb;
    });
  }, [myLessons, availableLessons]);

  const bookedTotalPages = Math.max(1, Math.ceil(allLessonsCombined.length / pageSize));
  const bookedPageItems = React.useMemo(() => {
    const start = pageBooked * pageSize;
    return allLessonsCombined.slice(start, start + pageSize);
  }, [allLessonsCombined, pageBooked, pageSize]);

  // Helpers for student daily cap and date checks must be declared before use
  const bookedCountOnDate = React.useCallback((iso: string) => {
    return myLessons.filter((l: { dateTime: string }) => sameAmsterdamDay(l.dateTime, iso)).length;
  }, [myLessons]);
  const isDateCapped = React.useCallback((iso: string) => bookedCountOnDate(iso) >= effectiveDailyCap, [bookedCountOnDate, effectiveDailyCap]);
  const isTodayCapped = React.useMemo(() => bookedCountOnDate(new Date().toISOString()) >= effectiveDailyCap, [bookedCountOnDate, effectiveDailyCap]);
  const selectedDateCapped = React.useMemo(() => selectedAvailDate ? bookedCountOnDate(`${selectedAvailDate}T12:00:00`) >= effectiveDailyCap : false, [selectedAvailDate, bookedCountOnDate, effectiveDailyCap]);

  const availableFilteredForTab = React.useMemo(() => {
    // Re-apply the same filters that were used before slicing
    return availableLessons
      .filter(l => new Date(l.dateTime) > new Date())
      .filter(l => {
        if (resolvedRole==='student' && !selectedAvailDate && isTodayCapped) {
          return !sameAmsterdamDay(l.dateTime, new Date().toISOString());
        }
        return true;
      })
      .filter(l => {
        if (resolvedRole==='student' && !selectedAvailDate && isDateCapped(l.dateTime)) return false;
        return true;
      })
      .sort((a,b)=> new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
  }, [availableLessons, resolvedRole, selectedAvailDate, isTodayCapped, isDateCapped]);
  const availableTotalPages = Math.max(1, Math.ceil(availableFilteredForTab.length / pageSize));
  const availablePageItems = React.useMemo(() => {
    const start = pageAvailable * pageSize;
    return availableFilteredForTab.slice(start, start + pageSize);
  }, [availableFilteredForTab, pageAvailable, pageSize]);

  const pastSorted = React.useMemo(() => {
    return [...pastLessons.filter((l: any) => new Date(l.dateTime) < new Date())].sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
  }, [pastLessons]);
  const pastTotalPages = Math.max(1, Math.ceil(pastSorted.length / pageSize));
  const pastPageItems = React.useMemo(() => {
    const start = pagePast * pageSize;
    return pastSorted.slice(start, start + pageSize);
  }, [pastSorted, pagePast, pageSize]);


  const bookLesson = async (lessonId: string | number) => {
    if (!canBookCancel) return;
    try {
      setBooking(String(lessonId));
      setError(null);
      const resp = await apiService.post<any>(`/api/lessons/${lessonId}/book`);
      setSuccessMsg(resp?.message || 'Lesson booked successfully');

      // Auto-download ICS if iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (isIOS) {
        window.location.href = `${apiService.getBaseUrl()}/api/lessons/${lessonId}/calendar/ics`;
      }

      setTimeout(() => setSuccessMsg(null), 3000);
      await refreshLessons();
    } catch (e: any) {
      const msg = e?.message || 'Failed to book lesson';
      setError(msg);
    } finally {
      setBooking(null);
    }
  };

  const cancelLesson = async (lessonId: string) => {
    if (!canBookCancel) return;
    const confirmed = window.confirm('Are you sure you want to cancel this lesson?');
    if (!confirmed) return;
    try {
      setCancelling(lessonId);
      setError(null);
      const resp = await apiService.post<any>(`/api/lessons/${lessonId}/cancel`);
      setSuccessMsg(resp?.message || 'Lesson cancelled successfully');
      setTimeout(() => setSuccessMsg(null), 3000);
      await refreshLessons();
    } catch (e: any) {
      setError(e?.message || 'Failed to cancel lesson');
    } finally {
      setCancelling(null);
    }
  };

  const hasAssignedTeacher = Boolean(user?.assignedTeacherId);

  const renderTabs = () => {
    return (
      <TabGroup role="tablist" aria-label="Lesson View Tabs">
        <Tab 
          role="tab"
          aria-selected={listTab === 'all'}
          $active={listTab === 'all'} 
          onClick={() => setListTab('all')}
        >
          {t('portal.lessons.tabs.all', 'All')}
        </Tab>
        {resolvedRole !== 'teacher' && (
          <>
            <Tab 
              role="tab"
              aria-selected={listTab === 'available'}
              $active={listTab === 'available'} 
              onClick={() => setListTab('available')}
            >
              {t('portal.lessons.tabs.available', 'Available')}
            </Tab>
          </>
        )}
      </TabGroup>
    );
  };

  return (
    <PageWrapper>
      <ContentWrapper>
        {successMsg && (
          <div style={{ position: 'fixed', top: 90, right: 24, background: 'linear-gradient(135deg,#11998e,#38ef7d)', color: 'white', padding: '0.6rem 0.9rem', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.15)', zIndex: 3000, fontWeight: 700 }}>{successMsg}</div>
        )}
        {error && (
          <div style={{ position: 'fixed', top: 90, right: 24, background: 'linear-gradient(135deg,#fc4a1a,#f7b733)', color: 'white', padding: '0.6rem 0.9rem', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.15)', zIndex: 3000, fontWeight: 700 }}>{error}</div>
        )}

        <PageHeader>
          <h1>{t('portal.lessons.title')}</h1>
          {resolvedRole === 'teacher' && (
            <BulkButton onClick={() => setIsBulkModalOpen(true)}>
              {t('portal.lessons.bulk_create.title')}
            </BulkButton>
          )}
        </PageHeader>

        <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {renderTabs()}
          <FilterToggleBtn 
            $active={showFiltersMobile} 
            onClick={() => setShowFiltersMobile(!showFiltersMobile)}
          >
            <FilterIcon fontSize="small" />
            {showFiltersMobile ? t('portal.common.hide_filters', 'Hide Filters') : t('portal.common.show_filters', 'Show Filters')}
          </FilterToggleBtn>

          <FilterWrapper $show={showFiltersMobile}>
            <LessonListFilters
              lessonFilter={listTab}
              teacherId="all"
              selectedDate={selectedAvailDate}
              startDate={startDate}
              endDate={endDate}
              lessonType={lessonType}
              licenseType={licenseType}
              teacherOptions={[]} // not needed for student/teacher view usually, or can be added if needed
              onLessonFilterChange={(next) => setListTab(next as LessonFilter)}
              onTeacherChange={() => {}}
              onDateChange={(next) => setSelectedAvailDate(next)}
              onStartDateChange={(next) => setStartDate(next)}
              onEndDateChange={(next) => setEndDate(next)}
              onLessonTypeChange={(next) => setLessonType(next)}
              onLicenseTypeChange={(next) => setLicenseType(next)}
              onApplyFilters={() => { applyFilters(); setShowFiltersMobile(false); }}
              onResetFilters={resetFilters}
              allowedStatuses={resolvedRole === 'student' && listTab === 'available' ? ['available'] : undefined}
            />
          </FilterWrapper>
        </div>

        {listTab==='all' && (
          <SectionCard id="all">
            <div className="section-header">
              {resolvedRole === 'student' && <p>All your lessons</p>}
            </div>
            {lessonsLoading ? (
              <div style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--text-light)' }}>Loading lessons…</div>
            ) : allLessonsCombined.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--text-light)' }}>No lessons found.</div>
            ) : (
              <>
                {bookedPageItems.map((lesson) => {
                  const isPassed = new Date(lesson.dateTime) < new Date();
                  return (
                    <AdvancedLessonCard
                      key={String(lesson.id)}
                      lesson={lesson}
                      mode={isPassed ? 'passed' : 'booked'}
                      showTeacherName={isStudent}
                      onClick={() => navigate(lessonDetailPath(lesson.id))}
                    />
                  );
                })}
              </>
            )}
            <BottomPager
              page={pageBooked}
              totalPages={bookedTotalPages}
              onPrev={() => setPageBooked(p => Math.max(0, p - 1))}
              onNext={() => setPageBooked(p => Math.min(bookedTotalPages - 1, p + 1))}
              loading={lessonsLoading}
            />
          </SectionCard>
        )}

        {listTab==='available' && (
          <SectionCard id="available-lessons" tabIndex={-1}>
            <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                {roleMode==='student' && <p>Pick a free slot to schedule your next lesson</p>}
              </div>
            </div>
            {lessonsLoading ? (
              <div style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--text-light)' }}>Loading available lessons…</div>
            ) : roleMode==='student' && !hasAssignedTeacher ? (
              <div style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--text-light)' }}>
                Contact your admin or teacher to assign you to a teacher to be able to book a lesson.
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--text-light)' }}>⚠️ {error}</div>
            ) : availableLessons.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--text-light)' }}>No available lessons right now. Please check back later.</div>
            ) : (
              <div>
                {roleMode==='student' && selectedAvailDate && selectedDateCapped ? (
                  <div aria-live="polite" style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--text-light)' }}>
                    Daily limit reached for this date (2/2). Please pick another day.
                  </div>
                ) : (
                  <>
                    {availablePageItems.map((lesson) => (
                      <AdvancedLessonCard
                        key={`avail-${lesson.id}`}
                        lesson={lesson}
                        mode="available"
                        showTeacherName={isStudent}
                        onClick={() => navigate(lessonDetailPath(lesson.id))}
                        onBook={isStudent ? bookLesson : undefined}
                        isBooking={booking === lesson.id}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
            <BottomPager
              page={pageAvailable}
              totalPages={availableTotalPages}
              onPrev={() => setPageAvailable(p => Math.max(0, p - 1))}
              onNext={() => setPageAvailable(p => Math.min(availableTotalPages - 1, p + 1))}
              loading={lessonsLoading}
            />
          </SectionCard>
        )}

      </ContentWrapper>
      <BulkLessonCreateModal
        open={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        onSuccess={() => {
          setSuccessMsg(t('portal.lessons.bulk_create.success'));
          refreshLessons();
          setTimeout(() => setSuccessMsg(null), 3000);
        }}
      />
    </PageWrapper>
  );
};

export default UnifiedLessons;
